import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface UserEmotionQuestionnaireProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userEmotions: { gratitude: number; sadness: number; anger: number; fear: number }) => void;
  speaker: string;
  text: string;
  caseId?: string;
  turnIndex?: number;
  existingReport?: { gratitude: number; sadness: number; anger: number; fear: number } | null;
}

const UserEmotionQuestionnaire: React.FC<UserEmotionQuestionnaireProps> = ({
  isOpen,
  onClose,
  onSubmit,
  speaker,
  text,
  caseId,
  turnIndex,
  existingReport = null,
}) => {
  const [userEmotions, setUserEmotions] = useState({
    gratitude: 0,
    sadness: 0,
    anger: 0,
    fear: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with existing report if available
  useEffect(() => {
    if (existingReport) {
      setUserEmotions(existingReport);
    } else {
      setUserEmotions({ gratitude: 0, sadness: 0, anger: 0, fear: 0 });
    }
  }, [existingReport, isOpen]);

  const handleEmotionChange = (emotion: string, value: number) => {
    setUserEmotions(prev => ({
      ...prev,
      [emotion]: value
    }));
  };

  const handleSubmit = async () => {
    if (!caseId || turnIndex === undefined) {
      onSubmit(userEmotions);
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.submitUserEmotionReport({
        caseId,
        turnIndex,
        userEmotions
      });
      onSubmit(userEmotions);
      onClose();
    } catch (error) {
      console.error('Failed to submit user emotion report:', error);
      // Still call onSubmit for local state management
      onSubmit(userEmotions);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEmotionColor = (value: number) => {
    if (value === 0) return 'bg-gray-100 text-gray-600';
    if (value === 1) return 'bg-red-100 text-red-800';
    if (value === 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getEmotionIntensity = (value: number) => {
    if (value === 0) return 'No Intensity';
    if (value === 1) return 'Low';
    if (value === 2) return 'Medium';
    return 'High';
  };

  const emotions = [
    { key: 'gratitude', label: 'Gratitude', description: 'Thankfulness, appreciation, relief, positive feelings' },
    { key: 'sadness', label: 'Sadness', description: 'Sorrow, grief, melancholy' },
    { key: 'anger', label: 'Anger', description: 'Frustration, irritation, rage' },
    { key: 'fear', label: 'Fear', description: 'Anxiety, worry, apprehension' },
  ];

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Rate the Emotions in This Dialogue
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Speaker:</strong> {speaker}
                  </p>
                  <p className="text-sm text-gray-800">
                    <strong>Text:</strong> "{text}"
                  </p>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Please rate the intensity of each emotion on a scale of 0-3:
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {emotions.map((emotion) => (
                      <div key={emotion.key} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 capitalize">
                              {emotion.label}
                            </h4>
                            <p className="text-xs text-gray-500">{emotion.description}</p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getEmotionColor(userEmotions[emotion.key as keyof typeof userEmotions])}`}>
                            {userEmotions[emotion.key as keyof typeof userEmotions]}/3
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          {[0, 1, 2, 3].map((value) => (
                            <label key={value} className="flex items-center">
                              <input
                                type="radio"
                                name={emotion.key}
                                value={value}
                                checked={userEmotions[emotion.key as keyof typeof userEmotions] === value}
                                onChange={() => handleEmotionChange(emotion.key, value)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-700">{value}</span>
                            </label>
                          ))}
                        </div>
                        
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(userEmotions[emotion.key as keyof typeof userEmotions] / 3) * 100}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {getEmotionIntensity(userEmotions[emotion.key as keyof typeof userEmotions])} Intensity
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Ratings'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserEmotionQuestionnaire;
